/**
 * MFA helper functions.
 * Pure functions for enrollment predicates, display names, error checking, and consent hashing.
 * Ported from ss-ui-client/src/global/step-up-authentication/helpers.jsx
 * and shipstation-lite/src/features/mfa/mfa-helpers.ts
 */

import { ADDITIONAL_VERIFICATION_FLOWS, TOKEN_TIMEOUT_ERROR, MFA_CANCELLED_ERROR } from './mfa-constants';

import type { MfaEnrollment, MfaConsent, HashedMfaConsent } from './mfa-types';

// ---- Enrollment Predicates ----

/**
 * Check if an enrollment is email-based.
 */
export const getIsEmail = (enrollment: MfaEnrollment): boolean => {
  return enrollment.id.includes('email');
};

/**
 * Check if an enrollment is SMS-based.
 */
export const getIsSms = (enrollment: MfaEnrollment): boolean => {
  return enrollment.id.includes('sms');
};

/**
 * Check if an enrollment is voice-based.
 */
export const getIsVoice = (enrollment: MfaEnrollment): boolean => {
  return enrollment.id.includes('voice');
};

/**
 * Check if an enrollment is TOTP authenticator-based.
 */
export const getIsAuthenticator = (enrollment: MfaEnrollment): boolean => {
  return enrollment.id.includes('totp');
};

// ---- Display Name ----

/**
 * Get the display name for an enrollment (email address or masked phone).
 * Format: "(***) *** - 1234" for phones, bare email address for email.
 */
export const getEnrollmentDisplayName = (enrollment: MfaEnrollment): string => {
  if (getIsEmail(enrollment)) {
    return enrollment.name;
  }

  if (getIsSms(enrollment) || getIsVoice(enrollment)) {
    const lastFour = enrollment.name.slice(-4);
    return `(***) *** - ${lastFour}`;
  }

  return '';
};

// ---- Error Predicates ----

/**
 * Check if an API error response indicates MFA is needed.
 */
export const checkResponseIsMfaNeeded = (error: unknown): boolean => {
  const err = error as { response?: { data?: { Code?: string } } };
  return err?.response?.data?.Code === ADDITIONAL_VERIFICATION_FLOWS.mfaRequired;
};

/**
 * Check if an API error response indicates email verification is required.
 */
export const checkResponseIsEmailVerificationRequired = (error: unknown): boolean => {
  const err = error as { response?: { data?: { Code?: string } } };
  return err?.response?.data?.Code === ADDITIONAL_VERIFICATION_FLOWS.emailVerificationRequired;
};

/**
 * Check if an error is the user-cancelled MFA error.
 */
export const checkIsErrorMessageUserCancelledMfa = (error: unknown): boolean => {
  const err = error as { message?: string };
  return err?.message === MFA_CANCELLED_ERROR;
};

/**
 * Throw the error if it indicates additional verification is needed (MFA or email).
 * This is used in API calls to allow the error to propagate and be handled by interceptors.
 */
export const throwErrorIfAdditionalVerificationNeeded = (response: unknown): void => {
  if (checkResponseIsMfaNeeded(response) || checkResponseIsEmailVerificationRequired(response)) {
    throw response;
  }
};

/**
 * Check if an error is a token timeout error (mfaToken expired).
 */
export const checkIsTokenTimeoutError = (error: unknown): boolean => {
  const err = error as { response?: { data?: { code?: number; message?: string } } };
  const data = err?.response?.data;
  if (!data) {
    return false;
  }

  const { code, message } = data;
  return (
    code === TOKEN_TIMEOUT_ERROR.code ||
    Boolean(message?.match(new RegExp(TOKEN_TIMEOUT_ERROR.message))) ||
    Boolean(message?.match(new RegExp(TOKEN_TIMEOUT_ERROR.message2)))
  );
};

// ---- Consent Hashing ----

/**
 * Hash consent text using SHA-256.
 * Input is exactly `${consent.text}${disclaimerSuffix}` where disclaimerSuffix
 * is a leading-space-prefixed formatted disclaimer or empty string.
 * Returns hex-encoded lowercase hashes.
 */
export const hashConsents = async (consents: MfaConsent[], disclaimerSuffix: string): Promise<HashedMfaConsent[]> => {
  const hashed = await Promise.all(
    consents.map(async (consent) => {
      const input = `${consent.text}${disclaimerSuffix}`;
      const encoder = new TextEncoder();
      const data = encoder.encode(input);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
      return {
        text_hash: hashHex,
        accepted: consent.accepted,
      };
    }),
  );
  return hashed;
};
